<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Modal from 'dashboard/components/Modal.vue';
import KanbanCustomFieldsTab from './KanbanCustomFieldsTab.vue';
import kanbanAPI from 'dashboard/api/kanban';
import offersAPI from 'dashboard/api/offers';
import MessageAPI from 'dashboard/api/inbox/message';
import { emitter } from 'shared/helpers/mitt';

const { t } = useI18n();
const store = useStore();

const contactGetter = useMapGetter('contacts/getContact');

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  conversationData: {
    type: Object,
    default: null,
  },
  loadingConversation: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
  notes: {
    type: Array,
    default: () => [],
  },
  checklistItems: {
    type: Array,
    default: () => [],
  },
  attachments: {
    type: Array,
    default: () => [],
  },
});

// Computed para formatar data
const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

// Computed para obter dados combinados da conversa (prioriza dados do item)
const conversationInfo = computed(() => {
  console.log('DEBUG - props.item:', props.item);
  console.log('DEBUG - props.conversationData:', props.conversationData);

  // Primeiro tenta usar os dados da conversa do item (preferência)
  const conversationData = props.item?.conversation;
  if (conversationData) {
    const result = {
      id: conversationData.id,
      display_id: conversationData.display_id,
      status: conversationData.status,
      last_activity_at: conversationData.last_activity_at,
      unread_count: conversationData.unread_count,
      label_list: conversationData.label_list || [],
      contact: conversationData.contact,
      inbox: conversationData.inbox,
      custom_attributes: conversationData.custom_attributes,
    };
    console.log('DEBUG - conversationInfo from item.conversation:', result);
    return result;
  }

  // Fallback para a prop conversationData (compatibilidade)
  if (props.conversationData) {
    console.log('DEBUG - conversationInfo from props.conversationData:', props.conversationData);
    return props.conversationData;
  }

  console.log('DEBUG - conversationInfo: null');
  return null;
});

const contactId = computed(() => conversationInfo.value?.contact?.id);
const contact = computed(() => contactGetter.value(contactId.value));

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

// Watch para carregar dados do contato quando o contactId muda
watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

// Watch para buscar counts quando o item muda
watch(() => props.item?.id, (newItemId, oldItemId) => {
  if (newItemId && newItemId !== oldItemId) {
    fetchItemCounts(newItemId);
    fetchOffersData();
  }
}, { immediate: false });

// Computed para obter os valores dos atributos do contato do store
const contactAttributeValues = computed(() => {
  console.log('DEBUG - contact from store:', contact.value);
  console.log('DEBUG - contact custom_attributes:', contact.value?.custom_attributes);

  if (!contact.value?.custom_attributes) {
    console.log('DEBUG - No contact custom_attributes found');
    return [];
  }

  const attributes = Object.entries(contact.value.custom_attributes)
    .map(([key, value]) => ({
      key,
      value,
      displayName: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }));

  console.log('DEBUG - contactAttributeValues:', attributes);
  return attributes;
});

// Computed para obter os valores dos atributos da conversa diretamente do objeto
const conversationAttributeValues = computed(() => {
  console.log('DEBUG - conversation custom_attributes:', conversationInfo.value?.custom_attributes);

  if (!conversationInfo.value?.custom_attributes) {
    console.log('DEBUG - No conversation custom_attributes found');
    return [];
  }

  const attributes = Object.entries(conversationInfo.value.custom_attributes)
    .map(([key, value]) => ({
      key,
      value,
      displayName: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }));

  console.log('DEBUG - conversationAttributeValues:', attributes);
  return attributes;
});

// Computed para obter os valores dos custom_attributes do item
const itemCustomAttributeValues = computed(() => {
  console.log('DEBUG - item custom_attributes:', props.item?.item_details?.custom_attributes);

  if (!props.item?.item_details?.custom_attributes) {
    console.log('DEBUG - No item custom_attributes found');
    return [];
  }

  const attributes = Object.entries(props.item.item_details.custom_attributes)
    .map(([key, value]) => ({
      key,
      value,
      displayName: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      isArray: Array.isArray(value)
    }));

  console.log('DEBUG - itemCustomAttributeValues:', attributes);
  return attributes;
});

// Estado para controlar os accordions
const isItemInfoOpen = ref(false);
const isConversationOpen = ref(false);
const isCustomFieldsOpen = ref(false);

// Estado para controlar estatísticas
const statisticsCollapsed = ref(false);

// Estado para armazenar os counts do backend
const itemCounts = ref({
  notes_count: 0,
  checklist_count: 0,
  attachments_count: 0
});
const offersFromAPI = ref([]);
const showOrderbumpModal = ref(false);
const sendingOrderbumpMessage = ref(false);
const orderbumpMessageSent = ref(false);
const selectedOrderbumpProduct = ref(null);
const orderbumpMessage = ref('');

// Função para alternar estado das estatísticas
const toggleStatistics = () => {
  statisticsCollapsed.value = !statisticsCollapsed.value;
};

// Função para buscar os counts do item
const fetchItemCounts = async (itemId) => {
  try {
    const response = await kanbanAPI.getCounts(itemId);
    itemCounts.value = response.data;
  } catch (error) {
    console.error('Erro ao buscar counts do item:', error);
    // Fallback para valores calculados localmente
    itemCounts.value = {
      notes_count: props.notes?.length || 0,
      checklist_count: props.checklistItems?.length || 0,
      attachments_count: props.attachments?.length || 0
    };
  }
};

// Computeds para estatísticas
const totalChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.length;
});

const completedChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.filter(item => item.completed).length;
});

const calculateStageChanges = (activities) => {
  if (!activities || !Array.isArray(activities)) return 0;
  return activities.filter(activity => activity.type === 'stage_changed').length;
};

const stageChangesCount = computed(() => {
  return calculateStageChanges(props.item.activities);
});

const itemStatistics = computed(() => {
  const timeInStage = props.item.stage_entered_at
    ? formatDistanceToNow(new Date(props.item.stage_entered_at), { locale: ptBR, addSuffix: false })
    : t('KANBAN.STATISTICS.NOT_AVAILABLE');

  // Usar os counts do backend, com fallback para valores calculados localmente
  const noteCount = itemCounts.value.notes_count;
  const checklistProgressText = `${completedChecklistItems.value}/${itemCounts.value.checklist_count}`;
  const attachmentCount = itemCounts.value.attachments_count;

  return [
    { icon: 'lock', label: t('KANBAN.STATISTICS.TIME_IN_STAGE'), value: timeInStage },
    { icon: 'comment-add', label: t('KANBAN.STATISTICS.NOTES'), value: noteCount },
    { icon: 'checkmark-circle', label: t('KANBAN.STATISTICS.CHECKLIST'), value: checklistProgressText },
    { icon: 'attach', label: t('KANBAN.STATISTICS.ATTACHMENTS'), value: attachmentCount },
    { icon: 'arrow-right', label: t('KANBAN.STATISTICS.STAGE_CHANGES'), value: stageChangesCount.value },
  ];
});

const emit = defineEmits(['navigate-to-conversation', 'context-menu', 'stage-click', 'update:item', 'item-updated']);

// Oferta do item (marcada como default ou primeira) para exibir no topo
const displayOffer = computed(() => {
  const offers = props.item?.item_details?.offers;
  if (!offers?.length) return null;
  const defaultOffer = offers.find(o => o.default === true);
  return defaultOffer ?? offers[0];
});

const fetchOffersData = async () => {
  try {
    const response = await offersAPI.getOffers();
    offersFromAPI.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Erro ao buscar ofertas:', error);
    offersFromAPI.value = [];
  }
};

const displayOfferFromAPI = computed(() => {
  if (!displayOffer.value?.id) return null;
  const offerId = Number(displayOffer.value.id);
  return offersFromAPI.value.find(offer => Number(offer.id) === offerId) || null;
});

const displayOfferOrderbumpProducts = computed(() => {
  if (!displayOffer.value) return [];

  const orderbumpIds = Array.isArray(displayOfferFromAPI.value?.additional_data?.orderbump_offer_ids)
    ? displayOfferFromAPI.value.additional_data.orderbump_offer_ids
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0)
    : [];

  if (!orderbumpIds.length) return [];

  return orderbumpIds
    .map(orderbumpId => offersFromAPI.value.find(offer => Number(offer.id) === orderbumpId))
    .filter(Boolean);
});

const orderbumpOfferedIds = computed(() => {
  const offeredIds = props.item?.item_details?.orderbump_offered_ids;
  if (!Array.isArray(offeredIds)) return [];
  return offeredIds.map(id => String(id));
});

const isOrderbumpAlreadyOffered = orderbumpProduct => {
  if (!orderbumpProduct?.id) return false;
  return orderbumpOfferedIds.value.includes(String(orderbumpProduct.id));
};

const markOrderbumpAsOffered = async orderbumpProduct => {
  if (!props.item?.id || !orderbumpProduct?.id) return;
  if (isOrderbumpAlreadyOffered(orderbumpProduct)) return;

  const nextOfferedIds = [...orderbumpOfferedIds.value, String(orderbumpProduct.id)];
  const payload = {
    ...(props.item || {}),
    item_details: {
      ...(props.item?.item_details || {}),
      orderbump_offered_ids: nextOfferedIds,
    },
  };

  const { data } = await kanbanAPI.updateItem(props.item.id, payload);
  if (data) {
    if (props.item?.item_details) {
      props.item.item_details.orderbump_offered_ids = nextOfferedIds;
    }
    emit('update:item', data);
    emit('item-updated');
  }
};

const openOrderbumpModal = (orderbumpProduct) => {
  selectedOrderbumpProduct.value = orderbumpProduct;
  orderbumpMessageSent.value = false;
  orderbumpMessage.value = `Tenho uma oferta especial para você: ${orderbumpProduct.title || orderbumpProduct.description} por ${formatCurrencyValue(orderbumpProduct.value, orderbumpProduct.currency)}.${orderbumpProduct.product_link ? ` Confira aqui: ${orderbumpProduct.product_link}` : ''}`;
  showOrderbumpModal.value = true;
};

const closeOrderbumpModal = () => {
  if (sendingOrderbumpMessage.value) return;
  showOrderbumpModal.value = false;
  orderbumpMessageSent.value = false;
  selectedOrderbumpProduct.value = null;
  orderbumpMessage.value = '';
};

const sendOrderbumpMessage = async () => {
  const conversationId = conversationInfo.value?.display_id || conversationInfo.value?.id;
  const inboxId = conversationInfo.value?.inbox?.id;

  if (!conversationId || !inboxId || !orderbumpMessage.value.trim()) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.ERROR_MISSING_DATA'),
      action: { type: 'error' },
    });
    return;
  }

  try {
    sendingOrderbumpMessage.value = true;
    await MessageAPI.create({
      conversationId,
      message: orderbumpMessage.value.trim(),
      private: false,
      message_type: 'outgoing',
      inbox_id: inboxId,
    });

    emitter.emit('newToastMessage', {
      message: t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SUCCESS_SENT'),
      action: { type: 'success' },
    });
    if (selectedOrderbumpProduct.value?.id) {
      await markOrderbumpAsOffered(selectedOrderbumpProduct.value);
    }
    orderbumpMessageSent.value = true;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.ERROR_SEND'),
      action: { type: 'error' },
    });
  } finally {
    sendingOrderbumpMessage.value = false;
  }
};

const formatCurrencyValue = (value, currency) => {
  if (!value) return '-';
  if (!currency) return value.toString();
  const locale = currency.locale || 'pt-BR';
  const code = currency.code || 'BRL';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    return value.toString();
  }
};

// Carregar dados do contato quando o componente for montado
onMounted(() => {
  getContactDetails();
  fetchOffersData();
  // Buscar counts do item se ele existir
  if (props.item?.id) {
    fetchItemCounts(props.item.id);
  }
});
</script>

<template>
  <div class="w-full">

    <!-- Bloco da oferta (estilo igual ao bloco de ofertas dos detalhes do item) -->
    <div v-if="displayOffer" class="mx-2 mb-4">
      <div class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
        <div class="flex items-center gap-3 py-3 px-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80">
          <span class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-woot-100 dark:bg-woot-900/40 text-woot-600 dark:text-woot-400">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
              <circle cx="8" cy="21" r="1"/>
              <circle cx="19" cy="21" r="1"/>
              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
            </svg>
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {{ t('KANBAN.BASIC_DATA_PANEL.ITEM_OFFER') }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {{ t('KANBAN.BASIC_DATA_PANEL.FEATURED_OFFER') }}
            </p>
          </div>
          <span class="flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full bg-woot-100 dark:bg-woot-900/50 text-woot-700 dark:text-woot-300">
            1
          </span>
        </div>
        <div class="p-4">
          <div class="flex items-start gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
            <div
              v-if="displayOffer.image_url"
              class="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 ring-1 ring-slate-200/50 dark:ring-slate-600/50"
            >
              <img
                :src="displayOffer.image_url"
                :alt="displayOffer.description"
                class="w-full h-full object-cover"
              />
            </div>
            <div
              v-else
              class="w-12 h-12 flex-shrink-0 rounded-lg flex items-center justify-center bg-woot-100 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400 ring-1 ring-woot-200/50 dark:ring-woot-700/50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                {{ displayOffer.description }}
              </h3>
              <a
                v-if="displayOffer.product_link"
                :href="displayOffer.product_link"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1 text-xs text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 truncate mt-0.5"
              >
                <fluent-icon icon="link" size="12" class="shrink-0" />
                <span class="truncate">{{ displayOffer.product_link }}</span>
              </a>
              <span class="block text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
                {{ formatCurrencyValue(displayOffer.value, displayOffer.currency) }}
              </span>
              <div v-if="displayOfferOrderbumpProducts.length" class="mt-3 flex flex-wrap gap-2">
                <button
                  v-for="orderbumpProduct in displayOfferOrderbumpProducts"
                  :key="orderbumpProduct.id"
                  type="button"
                  class="inline-flex max-w-full min-w-0 overflow-hidden items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors"
                  :class="isOrderbumpAlreadyOffered(orderbumpProduct)
                    ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-300 cursor-default'
                    : 'border-woot-200 dark:border-woot-700 bg-woot-50 dark:bg-woot-900/20 text-woot-700 dark:text-woot-300 cursor-pointer hover:bg-woot-100 dark:hover:bg-woot-900/35'"
                  :disabled="isOrderbumpAlreadyOffered(orderbumpProduct)"
                  @click="openOrderbumpModal(orderbumpProduct)"
                >
                  <fluent-icon :icon="isOrderbumpAlreadyOffered(orderbumpProduct) ? 'checkmark-circle' : 'add-circle'" size="14" class="shrink-0" />
                  <span class="truncate max-w-full">
                    {{
                      isOrderbumpAlreadyOffered(orderbumpProduct)
                        ? `Order bump #${orderbumpProduct.id} já foi oferecido`
                        : t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.BUTTON_LABEL', { id: orderbumpProduct.id, title: orderbumpProduct.description || orderbumpProduct.title })
                    }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bloco de infos do contato (estilo igual aos blocos Oferta e Agentes) -->
    <div v-if="contact && conversationInfo" class="mx-2 mb-4">
      <div class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
        <div class="flex items-center gap-3 py-3 px-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/80">
          <span class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-woot-100 dark:bg-woot-900/40 text-woot-600 dark:text-woot-400">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Contato
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              {{ contact.name }}
            </p>
          </div>
        </div>
        <div class="p-4">
          <div class="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
            <div class="relative flex-shrink-0">
              <img
                v-if="contact.thumbnail"
                :src="contact.thumbnail"
                :alt="contact.name"
                class="w-12 h-12 rounded-full object-cover ring-1 ring-slate-200/50 dark:ring-slate-600/50"
              />
              <div v-else class="w-12 h-12 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center ring-1 ring-slate-200/50 dark:ring-slate-600/50">
                <span class="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {{ contact.name?.charAt(0)?.toUpperCase() || '?' }}
                </span>
              </div>
              <div class="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white dark:ring-slate-800"
                   :class="{
                     'bg-green-500': contact.availability_status === 'online',
                     'bg-yellow-500': contact.availability_status === 'busy' || !contact.availability_status,
                     'bg-slate-400': contact.availability_status === 'offline'
                   }"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {{ contact.name }}
                </h3>
                <span class="text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="{
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': contact.availability_status === 'online',
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': contact.availability_status === 'busy' || !contact.availability_status,
                        'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400': contact.availability_status === 'offline'
                      }"
                >
                  {{ contact.availability_status === 'online' ? 'Online' : contact.availability_status === 'busy' ? 'Ocupado' : (contact.availability_status === 'offline' ? 'Offline' : 'Ausente') }}
                </span>
              </div>
              <div class="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400 mt-1 flex-wrap">
                <a
                  v-if="contact.email"
                  :href="`mailto:${contact.email}`"
                  class="flex items-center gap-1 hover:text-woot-500 dark:hover:text-woot-400 truncate max-w-[180px]"
                >
                  <fluent-icon icon="mail" size="12" class="shrink-0" />
                  <span class="truncate">{{ contact.email }}</span>
                </a>
                <a
                  v-if="contact.phone_number"
                  :href="`tel:${contact.phone_number}`"
                  class="flex items-center gap-1 hover:text-woot-500 dark:hover:text-woot-400"
                >
                  <fluent-icon icon="call" size="12" class="shrink-0" />
                  <span>{{ contact.phone_number }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seção de Estatísticas -->
    <div class="item-statistics bg-white dark:bg-slate-800 rounded-lg p-3">
      <!-- Cabeçalho com botão de colapso -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 text-base font-medium">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chart-no-axes-column-icon lucide-chart-no-axes-column">
            <path d="M5 21v-6"/>
            <path d="M12 21V3"/>
            <path d="M19 21V9"/>
          </svg>
          <span>{{ t('KANBAN.STATISTICS.TITLE') }}</span>
        </div>
        <button
          type="button"
          @click="toggleStatistics"
          class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <fluent-icon
            :icon="statisticsCollapsed ? 'chevron-down' : 'chevron-up'"
            size="16"
          />
        </button>
      </div>

      <!-- Conteúdo colapsável -->
      <div v-show="!statisticsCollapsed" class="statistics-content transition-all duration-300 ease-in-out">
        <div class="flex flex-col gap-2">
          <div
            v-for="stat in itemStatistics"
            :key="stat.label"
            class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-md w-full"
          >
            <!-- Lado esquerdo: ícone + título -->
            <div class="flex items-center gap-1.5">
              <!-- Ícone inline para tempo na etapa -->
              <div v-if="stat.icon === 'lock'" class="flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock-icon lucide-clock text-slate-500 dark:text-slate-400">
                  <path d="M12 6v6l4 2"/>
                  <circle cx="12" cy="12" r="10"/>
                </svg>
              </div>
              <!-- Outros ícones continuam usando fluent-icon -->
              <fluent-icon
                v-else
                :icon="stat.icon"
                size="14"
                class="text-slate-500 dark:text-slate-400"
              />
              <span class="text-xs text-slate-500 dark:text-slate-400">
                {{ stat.label }}
              </span>
            </div>

            <!-- Lado direito: valor -->
            <span class="text-sm font-medium text-slate-800 dark:text-slate-200">
              {{ stat.value }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Conteúdo do painel -->
    <div class="pb-8 list-group px-2">
      <div class="flex flex-col gap-3">
        <!-- Seção de Informações do Item -->
        <AccordionItem
          :title="t('KANBAN.BASIC_DATA_PANEL.ITEM_INFO') || 'Informações do Item'"
          :is-open="isItemInfoOpen"
          compact
          @toggle="isItemInfoOpen = !isItemInfoOpen"
        >
          <div class="p-2">
            <!-- Informações básicas do item -->
            <div class="mb-4">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {{ item.title || item.name || 'Item' }}
                </h3>
                <span class="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded">
                  {{ currentStageName || 'Sem estágio' }}
                </span>
              </div>
              <p v-if="itemDescription" class="text-sm text-slate-600 dark:text-slate-400">
                {{ itemDescription }}
              </p>
            </div>

            <!-- Atributos do Contato -->
            <div v-if="contactAttributeValues.length > 0" class="mb-4">
              <h4 class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                Atributos do Contato
              </h4>
              <div class="space-y-2">
                <div
                  v-for="attr in contactAttributeValues"
                  :key="attr.key"
                  class="flex justify-between items-center py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700"
                >
                  <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {{ attr.displayName }}
                  </span>
                  <span class="text-sm text-slate-900 dark:text-slate-100">
                    {{ attr.value }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Atributos da Conversa -->
            <div v-if="conversationAttributeValues.length > 0" class="mb-4">
              <h4 class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                Atributos da Conversa
              </h4>
              <div class="space-y-2">
                <div
                  v-for="attr in conversationAttributeValues"
                  :key="attr.key"
                  class="flex justify-between items-center py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700"
                >
                  <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {{ attr.displayName }}
                  </span>
                  <span class="text-sm text-slate-900 dark:text-slate-100">
                    {{ attr.value }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Atributos Customizados do Item -->
            <div v-if="itemCustomAttributeValues.length > 0" class="mb-4">
              <h4 class="text-xs font-medium text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                Atributos do Item
              </h4>
              <div class="space-y-2">
                <div
                  v-for="attr in itemCustomAttributeValues"
                  :key="attr.key"
                  class="py-2 px-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700"
                >
                  <div class="flex justify-between items-center mb-1">
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {{ attr.displayName }}
                    </span>
                  </div>
                  <!-- Se for array, mostra como lista -->
                  <div v-if="attr.isArray" class="mt-2">
                    <div class="flex flex-col gap-1">
                      <span
                        v-for="item in attr.value"
                        :key="item"
                        class="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full w-fit"
                      >
                        {{ item }}
                      </span>
                    </div>
                  </div>
                  <!-- Se não for array, mostra como texto normal -->
                  <div v-else class="text-sm text-slate-900 dark:text-slate-100">
                    {{ attr.value }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Mensagem quando não há atributos -->
            <div v-if="contactAttributeValues.length === 0 && conversationAttributeValues.length === 0 && itemCustomAttributeValues.length === 0" class="text-center py-4">
              <span class="text-sm text-slate-500 dark:text-slate-400">
                {{ t('KANBAN.CUSTOM_FIELDS.NO_ATTRIBUTES_FOUND') }}
              </span>
            </div>
          </div>
        </AccordionItem>


        <!-- Seção de Conversa Vinculada -->
        <AccordionItem
          v-if="conversationInfo"
          :title="t('KANBAN.BASIC_DATA_PANEL.CONVERSATION') || 'Conversa Vinculada'"
          :is-open="isConversationOpen"
          compact
          @toggle="isConversationOpen = !isConversationOpen"
        >
          <div class="p-2">
            <div class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200/30 dark:border-slate-700/30 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
               @click="$emit('navigate-to-conversation', $event, conversationInfo.display_id)"
               @contextmenu="$emit('context-menu', $event, conversationInfo.id)">
            <!-- Contato -->
            <div v-if="conversationInfo.contact" class="flex items-center gap-3 mb-2">
              <img
                v-if="conversationInfo.contact.thumbnail"
                :src="conversationInfo.contact.thumbnail"
                :alt="conversationInfo.contact.name"
                class="w-8 h-8 rounded-full object-cover"
              />
              <div v-else class="w-8 h-8 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center">
                <span class="text-xs font-medium text-slate-600 dark:text-slate-400">
                  {{ conversationInfo.contact.name?.charAt(0)?.toUpperCase() || '?' }}
                </span>
              </div>
              <div class="flex-1">
                <div class="flex items-center justify-between">
                  <div class="font-semibold text-sm text-slate-900 dark:text-slate-100">
                    <span class="text-xs text-slate-500 dark:text-slate-400 mr-1">#{{ conversationInfo.display_id }}</span>
                    {{ conversationInfo.contact.name || t('KANBAN.CONTACT_UNKNOWN') }}
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-1">
                <!-- Status -->
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
                  :class="{
                    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300':
                      conversationInfo.status === 'open',
                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300':
                      conversationInfo.status === 'pending',
                    'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300':
                      conversationInfo.status === 'resolved',
                  }"
                >
                  {{ conversationInfo.status }}
                </span>
                <!-- Unread count -->
                <span
                  v-if="conversationInfo.unread_count > 0"
                  class="flex items-center justify-center h-5 min-w-[1rem] px-1.5 text-xs font-medium bg-red-500 text-white rounded-full shadow-sm"
                >
                  {{ conversationInfo.unread_count > 9 ? '9+' : conversationInfo.unread_count }}
                </span>
              </div>
            </div>

            <!-- Labels -->
            <div v-if="conversationInfo.label_list && conversationInfo.label_list.length > 0" class="flex flex-wrap gap-1">
              <span
                v-for="label in conversationInfo.label_list"
                :key="label"
                class="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
              >
                {{ label }}
              </span>
            </div>
          </div>
          </div>
        </AccordionItem>

        <!-- Seção de Dados Adicionais -->
        <AccordionItem
          :title="t('KANBAN.CUSTOM_FIELDS.TITLE') || 'Dados Adicionais'"
          :is-open="isCustomFieldsOpen"
          compact
          @toggle="isCustomFieldsOpen = !isCustomFieldsOpen"
        >
          <div class="p-2">
            <KanbanCustomFieldsTab
              :item="item"
              :is-stacklab="isStacklab"
              @update:item="$emit('update:item', $event)"
              @item-updated="$emit('item-updated')"
            />
          </div>
        </AccordionItem>
      </div>
    </div>

    <Modal
      v-model:show="showOrderbumpModal"
      :on-close="closeOrderbumpModal"
    >
      <div class="p-6">
        <div
          v-if="orderbumpMessageSent"
          class="flex flex-col items-center justify-center py-8 text-center"
        >
          <div class="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <fluent-icon icon="checkmark" size="52" class="text-green-600 dark:text-green-400" />
          </div>
          <h3 class="mt-5 text-xl font-semibold text-slate-900 dark:text-slate-100">
            {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SUCCESS_SENT') }}
          </h3>
          <button
            type="button"
            class="mt-6 px-4 py-2 rounded-lg text-sm text-white bg-woot-500 hover:bg-woot-600"
            @click="closeOrderbumpModal"
          >
            {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.CANCEL') }}
          </button>
        </div>
        <div v-else>
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
            {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.MODAL_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {{ selectedOrderbumpProduct ? `#${selectedOrderbumpProduct.id} - ${selectedOrderbumpProduct.title || selectedOrderbumpProduct.description}` : '' }}
          </p>

          <div class="mt-4">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.MESSAGE_LABEL') }}
            </label>
            <textarea
              v-model="orderbumpMessage"
              rows="5"
              class="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              :placeholder="t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.MESSAGE_PLACEHOLDER')"
            />
          </div>

          <div class="mt-6 flex justify-end gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              :disabled="sendingOrderbumpMessage"
              @click="closeOrderbumpModal"
            >
              {{ t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.CANCEL') }}
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-lg text-sm text-white bg-woot-500 hover:bg-woot-600 disabled:opacity-60"
              :disabled="sendingOrderbumpMessage || !orderbumpMessage.trim()"
              @click="sendOrderbumpMessage"
            >
              {{ sendingOrderbumpMessage ? t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SENDING') : t('KANBAN.BASIC_DATA_PANEL.ORDER_BUMP.SEND') }}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-n-weak;
  }
}

/* Estilos para animação de colapso das estatísticas */
.item-statistics {
  /* Estilos já aplicados via classes Tailwind */
}

.statistics-content {
  overflow: hidden;
  transition: all 0.3s ease-in-out;
}

.statistics-content.collapsed {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.statistics-content.expanded {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}
</style>
